import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, Flower2, HandHeart, Euro, Search, X, CalendarDays, Sunset } from "lucide-react";
import { StaffPinGate } from "../components/StaffPinGate";
import { hasCheckinAuth, PIN_KEY } from "../lib/checkinApi";
import { fetchStaffConfig, fetchBookings, fetchBlocks, fetchEventsWeek, fetchHorseWeek, iso, mondayOf, addDays, weekDays, fmtDay, SERVICES } from "../lib/prenotazioniApi";
import { EventsPanel } from "../components/prenotazioni/EventsPanel";
import { HorsePanel } from "../components/prenotazioni/HorsePanel";
import { WeekGrid } from "../components/prenotazioni/WeekGrid";
import { BookingSheet } from "../components/prenotazioni/BookingSheet";
import { InstallHomeButton } from "../components/InstallHomeButton";
import { PricesPanel } from "../components/prenotazioni/PricesPanel";

const TABS = [["spa", "Spa", Flower2], ["massage", "Massaggi", HandHeart], ["events", "Eventi", CalendarDays], ["horse", "Cavallo", Sunset], ["prices", "Prezzi", Euro]];

export default function PrenotazioniPage() {
  const [authed, setAuthed] = useState(hasCheckinAuth());
  const [cfg, setCfg] = useState(null);
  const today = iso(new Date());
  const [monday, setMonday] = useState(mondayOf(today));
  const [tab, setTab] = useState("spa");
  const [data, setData] = useState({ spa: [], massage: [] });
  const [blocks, setBlocks] = useState({ spa: [], massage: [] });
  const [events, setEvents] = useState({ bookings: [], events: [] });
  const [horse, setHorse] = useState({ week: [], all: [] });
  const [loading, setLoading] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [q, setQ] = useState("");
  const days = useMemo(() => weekDays(monday), [monday]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const from = days[0], to = days[6];
    try {
      const [c, spa, massage, bs, bm, ev, hw, ha] = await Promise.all([cfg || fetchStaffConfig(), fetchBookings("spa", from, to), fetchBookings("massage", from, to), fetchBlocks("spa", from, to), fetchBlocks("massage", from, to),
        fetchEventsWeek(from, to).catch(() => ({ bookings: [], events: [] })), fetchHorseWeek(from, to).catch(() => []), fetchHorseWeek("", "").catch(() => [])]);
      setCfg(c); setData({ spa, massage }); setBlocks({ spa: bs, massage: bm }); setEvents(ev); setHorse({ week: hw, all: ha });
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(PIN_KEY); setAuthed(false); } else toast.error("Errore caricamento");
    } finally { setLoading(false); }
  }, [days, cfg]);
  useEffect(() => { if (authed) load(); }, [authed, monday]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!authed) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 60000);
    return () => clearInterval(t);
  }, [authed, load]);

  if (!authed) return <StaffPinGate title="Prenotazioni · Spa e Massaggi" onUnlock={() => setAuthed(true)} />;

  const svc = tab === "spa" || tab === "massage" ? tab : null;
  const horsePending = horse.all.filter((b) => b.status === "pending");
  const needle = q.trim().toLowerCase();
  const rows = svc ? data[svc].filter((b) => !needle || (b.name || "").toLowerCase().includes(needle) || (b.phone || "").replace(/\D/g, "").includes(needle.replace(/\D/g, "") || "\u0000")) : [];
  const counts = (s) => data[s].filter((b) => b.date === today && b.status !== "cancelled").length;

  return (
    <div className="min-h-screen bg-[#f7ebdf] text-[#2b1410]" data-testid="prenotazioni-page">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-30 bg-[#4a1c17] text-[#f7ebdf] px-3 py-2 space-y-2">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-8 w-8 rounded-full object-cover bg-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="flex-1 min-w-0">
            <div className="font-serif font-bold leading-tight">Prenotazioni</div>
            <div className="text-[11px] opacity-80 truncate" data-testid="today-summary">Oggi: Spa {counts("spa")} · Massaggi {counts("massage")} · Eventi {events.bookings.filter((b) => (b.slot_datetime || "").slice(0, 10) === today && b.status !== "cancelled").length} · Cavallo {horse.week.filter((b) => b.date === today && b.status === "confirmed").length}</div>
          </div>
          <div className="hidden sm:flex items-center gap-1 rounded-full bg-white/10 px-1">
            <button onClick={() => setMonday(addDays(monday, -7))} className="h-9 w-9 rounded-full hover:bg-white/10 inline-flex items-center justify-center" data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setMonday(mondayOf(today))} className="h-9 px-3 rounded-full text-xs font-bold capitalize hover:bg-white/10" data-testid="week-label">{fmtDay(days[0])} – {fmtDay(days[6])}</button>
            <button onClick={() => setMonday(addDays(monday, 7))} className="h-9 w-9 rounded-full hover:bg-white/10 inline-flex items-center justify-center" data-testid="week-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <InstallHomeButton appName="Prenotazioni" compact />
          <button onClick={() => load()} className="h-9 w-9 rounded-full bg-white/10 inline-flex items-center justify-center" data-testid="refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
        <div className="flex items-center gap-2">
          <nav className="flex gap-1 overflow-x-auto [scrollbar-width:none]">
            {TABS.map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1.5 shrink-0 ${tab === k ? "bg-white text-[#4a1c17]" : "bg-white/10 hover:bg-white/20"}`} data-testid={`tab-${k}`}><Icon className="h-3.5 w-3.5" /> {label}{k === "horse" && horsePending.length > 0 && <span className="ml-0.5 rounded-full bg-amber-500 text-white text-[10px] px-1.5" data-testid="tab-horse-badge">{horsePending.length}</span>}</button>
            ))}
          </nav>
          {svc && (
            <div className="ml-auto relative flex-1 max-w-xs hidden sm:block">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 opacity-60" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome o telefono" className="w-full h-9 rounded-full bg-white/10 pl-8 pr-8 text-sm placeholder:text-white/50 focus:outline-none focus:bg-white/20" data-testid="search" />
              {q && <button onClick={() => setQ("")} className="absolute right-2 top-2.5" data-testid="search-clear"><X className="h-4 w-4" /></button>}
            </div>
          )}
        </div>
        <div className="flex sm:hidden items-center gap-2">
          <div className="flex items-center gap-1 rounded-full bg-white/10 px-1 shrink-0">
            <button onClick={() => setMonday(addDays(monday, -7))} className="h-9 w-9 rounded-full hover:bg-white/10 inline-flex items-center justify-center" data-testid="week-prev-m"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setMonday(mondayOf(today))} className="h-9 px-2 rounded-full text-xs font-bold capitalize hover:bg-white/10" data-testid="week-label-m">{fmtDay(days[0])} – {fmtDay(days[6])}</button>
            <button onClick={() => setMonday(addDays(monday, 7))} className="h-9 w-9 rounded-full hover:bg-white/10 inline-flex items-center justify-center" data-testid="week-next-m"><ChevronRight className="h-4 w-4" /></button>
          </div>
          {svc && (
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 opacity-60" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca" className="w-full h-9 rounded-full bg-white/10 pl-8 pr-8 text-sm placeholder:text-white/50 focus:outline-none focus:bg-white/20" data-testid="search-m" />
              {q && <button onClick={() => setQ("")} className="absolute right-2 top-2.5" data-testid="search-clear-m"><X className="h-4 w-4" /></button>}
            </div>
          )}
        </div>
      </header>

      <main className="p-3 space-y-3">
        {!cfg ? <div className="py-20 text-center text-sm text-[#7c5a4e]">Carico…</div> : tab === "prices" ? (
          <PricesPanel prices={cfg.prices} onSaved={(p) => setCfg({ ...cfg, prices: p })} />
        ) : tab === "events" ? (
          <EventsPanel days={days} data={events} today={today} onChanged={() => load(true)} />
        ) : tab === "horse" ? (
          <HorsePanel pending={horsePending} week={horse.week} today={today} onChanged={() => load(true)} />
        ) : (
          <>
            {needle && (
              <ul className="rounded-2xl bg-white border border-[#e8dcc8] divide-y divide-[#f1e6da]" data-testid="search-results">
                {rows.length === 0 && <li className="p-3 text-sm italic text-[#7c5a4e]">Nessun risultato in questa settimana</li>}
                {rows.map((b) => <li key={b.id}><button onClick={() => setSheet({ booking: b })} className="w-full text-left p-3 text-sm flex justify-between"><span className="font-semibold">{b.name}</span><span className="text-[#7c5a4e] capitalize">{fmtDay(b.date)} · {cfg[svc].slots.find((s) => s.id === b.slot_id)?.start}</span></button></li>)}
              </ul>
            )}
            <WeekGrid svc={svc} days={days} slots={cfg[svc].slots} bookings={data[svc]} blocks={blocks[svc]} prices={cfg.prices} today={today}
              onCell={(day, slot) => setSheet({ day, slot })} onBooking={(b) => setSheet({ booking: b })} onBlock={(bl) => setSheet({ block: bl })} />
            <p className="text-[11px] text-[#9a8070] px-1">Tocca una casella libera per prenotare o chiudere · tocca una prenotazione per modificarla · {SERVICES[svc]} · ruota il telefono per vedere tutta la settimana.</p>
          </>
        )}
      </main>
      {sheet && svc && <BookingSheet svc={svc} ctx={sheet} slots={cfg[svc].slots} prices={cfg.prices} config={cfg[svc]} onClose={() => setSheet(null)} onChanged={() => load(true)} />}
    </div>
  );
}
